// src/pages/Admin/YearsView.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import PaginatedTable from '../../components/PaginatedTable';
import TitleWithBack from '../../components/common/TitleWithBack';

function YearsView() {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [tooltip, setTooltip] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchYears();
  }, []);

  const fetchYears = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/years-of-study');
      let data = Array.isArray(res.data) ? res.data : [];
      data.sort((a, b) => b.id - a.id);
      setYears(data);
    } catch (err) {
      setError('Error fetching years.');
    }
    setLoading(false);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const sortedYears = [...years].sort((a, b) => {
    const aVal = (a[sortColumn] || '').toString().toLowerCase();
    const bVal = (b[sortColumn] || '').toString().toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredYears = sortedYears.filter(y =>
    y.name.toLowerCase().includes(filter.toLowerCase())
  );

  const handleEdit = (id) => {
    navigate(`/admin/years-of-study/edit/${id}`);
  };

  const handleDelete = (id, canDelete) => {
    if (canDelete === false) {
      setTooltip('This year cannot be deleted.');
      return;
    }
    setDeleteId(id);
  };

  const confirmDeletion = async () => {
    try {
      await axios.delete(`/api/years-of-study/${deleteId}`);
      setTooltip('Year deleted successfully!');
      fetchYears();
      setDeleteId(null);
    } catch (err) {
      setTooltip('Error deleting year.');
      setDeleteId(null);
    }
  };

  const cancelDeletion = () => {
    setDeleteId(null);
  };

  const columns = [
    { key: 'id', header: <>ID</> },
    { key: 'name', header: <>Year</> },
    { key: 'actions', header: 'Actions' }
  ];

  const renderRow = (y) => (
    <>
      <td>{y.id}</td>
      <td>{y.name}</td>
      <td>
        <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(y.id)}>
          Edit
        </button>
        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(y.id, y.canDelete)}>
          Delete
        </button>
      </td>
    </>
  );

  return (
    <div className="container-fluid mt-2 px-4">
      <TitleWithBack title="View All Years of Study" />
      <div className="mb-3">
        <input 
          type="text"
          className="form-control"
          placeholder="Search by year..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
      </div>
      {loading ? (
        <p>Loading years...</p>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : filteredYears.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={filteredYears}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
      {deleteId && (
        <ConfirmationModal 
          title="Confirm Delete"
          message="Are you sure you want to delete this year?"
          onConfirm={confirmDeletion}
          onCancel={cancelDeletion}
        />
      )}
    </div>
  );
}

export default YearsView;
